import { useEffect, useState, useMemo } from 'react';
import { Button, Field, Groupper, Header, Icon, IconName, Input, Message, Modal, Pagination, Table, Toast } from 'react-frontier';
import { Dropdown, DropdownProps } from 'semantic-ui-react';
import { bindClick, bindFormChange } from '../SiteUtil';
import { InventoryType, SetLoading } from '../SiteClasses';
import { useBreadcrumbs, useUser } from '../hooks';
import { ICON_NAMES } from '../iconNames';
import Validator, { ValidatorFormRules } from '../Validator';
import UserAccess from '../UserAccess';
import API from '../API';

var PAGE_SIZE = 20;
interface InventoryTypeForm{
   type_name: string;
   type_icon: string;
}

var InventoryTypesList = ()=>{
   var { hasAccess } = useUser();
   var { setTitle } = useBreadcrumbs();
   var [types, setTypes] = useState<Partial<InventoryType>[]>(null);
   var [loading, setLoading] = useState<boolean>(false);
   var [filter, setFilter] = useState<string>('');
   var [searchTimeout, setSearchTimeout] = useState<any>(null);
   var [page, setPage] = useState<number>(0);
   var [loadError, setLoadError] = useState<string>(null);

   var [showCreate, setShowCreate] = useState<boolean>(false);
   var [showEdit, setShowEdit] = useState<boolean>(false);
   var [showDelete, setShowDelete] = useState<boolean>(false);
   var [selectedType, setSelectedType] = useState<Partial<InventoryType>>(null);
   var [form, setForm] = useState<InventoryTypeForm>({ type_name: '', type_icon: '' });
   var [modalPrompts, setModalPrompts] = useState<string[]>(null);
   var [open, setOpen] = useState(false);

   useEffect(()=>{
      setTitle('Tipos de inventario');
      get();
   }, []);

   var get = (get_page?: number, query?: string)=>{
      var p = get_page ?? page;
      var offset = p * PAGE_SIZE;
      setLoading(true);
      API.getInventoryTypes(query || null, offset, PAGE_SIZE).then(res=>{
         if(res.error) return setLoadError(res.message);
         setTypes(res.data);
      }).catch(()=>{
         setLoadError('Hubo un error al obtener los tipos de inventario. (INV-TYPE-LST)');
      }).finally(()=>{
         setLoading(false);
      });
   };

   var onSearchChange = (value: string)=>{
      setFilter(value);
      if(searchTimeout) clearTimeout(searchTimeout);
      if(value.length > 0 && value.length < 2) return;
      setSearchTimeout(setTimeout(()=>{
         setPage(0);
         get(0, value.length >= 2 ? value : null);
      }, 500));
   };

   var onPageChange = (p: number)=>{
      setLoading(true);
      setPage(p);
      get(p, filter.length >= 2 ? filter : null);
   };

   var openCreate = ()=>{
      setForm({ type_name: '', type_icon: '' });
      setModalPrompts(null);
      setShowCreate(true);
   };

   var openEdit = (type:   Partial<InventoryType>)=>{
      setSelectedType(type);
      setForm({ type_name: type.type_name, type_icon: type.type_icon as IconName || '' });
      setModalPrompts(null);
      setShowEdit(true);
   };

   var openDelete = (type: Partial<InventoryType>)=>{
      setSelectedType(type);
      setModalPrompts(null);
      setShowDelete(true);
   };

   var submitCreate = (setModalLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			type_name: [{
				rule: 'empty', label: 'Nombre'
			}],
			type_icon: [{
				rule: 'empty', label: 'Equipo'
			}]
		});
		setModalPrompts(prompts);
		if(!valid) return;  
		setLoading(true);

      setModalLoading(true);
      API.createInventoryType(form.type_name, form.type_icon).then(res=>{
         if(res.error) return setModalPrompts([res.message]);
         Toast.success('Equipo creado exitosamente');
         setShowCreate(false);
         setPage(0);
         get(0, filter.length >= 2 ? filter : null);
      }).catch(()=>{
         setModalPrompts(['Hubo un error creando el equipo']);
      }).finally(()=>{
         setLoading(false);
         setModalLoading(false);
      });
   };

   var submitEdit = (setModalLoading: SetLoading)=>{
			var { valid, prompts } = Validator(form, {
			type_name: [{
				rule: 'empty', prompt: 'Nombre'
			}],
			type_icon: [{
				rule: 'empty', prompt: 'Equipo'
			}]
		});
		setModalPrompts(prompts);
		if (!valid) return;  


		setLoading(true);
      setModalLoading(true);
      API.editInventoryType(selectedType.type_id, form.type_name, form.type_icon).then(res=>{
         if(res.error) return setModalPrompts([res.message]);
         Toast.success('Equipo actualizado');
			get(0, filter.length >= 2 ? filter : null);
      }).catch(()=>{
         setModalPrompts(['Hubo un error actualizando el equipo']);
      }).finally(()=>{
         setLoading(false);
			setShowEdit(false);
         setModalLoading(false);
      });
   };

   var submitDelete = (setModalLoading: SetLoading)=>{
      setModalLoading(true);
      API.deleteInventoryType(selectedType.type_id).then(res =>{
         if(res.error) return setModalPrompts([res.message]);
         Toast.success('Equipo eliminado');

         setShowDelete(false);
         setPage(0);
         get(0, filter.length >= 2 ? filter : null);
      }).catch(()=>{
         setModalPrompts(['Hubo un error eliminando el equipo']);
      }).finally(()=>{
         setModalLoading(false);
      });
   };

   var onFormChange = bindFormChange(form, setForm);

   var onIconChange = (_: any, data: DropdownProps)=>{
      setForm(prev=>({ ...prev, type_icon: (data.value as IconName) || '' }));
   };

   var iconOptions = useMemo(()=>
      ICON_NAMES.map(iconName =>({
         key: iconName,
         value: iconName,
         text: iconName,
         icon: <Icon name={iconName} />,
      })
   ), []);

   if(loadError) return <Header text='Error' subtext={loadError} />;
   if(!types) return <Header text='Cargando datos' loading />;

   var shown_types = types || [];

   return <div>
      <Groupper
         title={'Tipos de inventario'}
         titleRight={hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY) && (
            <Button iconName='plus-circle' color='black' text='Agregar' size='small' onClick={openCreate} />
         )}
         style={{ marginTop: 20 }}
         width={600}
      >
         <Input placeholder='Buscar tipo de inventario...' value={filter} onChange={onSearchChange} />
         <Table striped fitted
            className='last top'
            style={{ marginTop: 15 }}
            collapsingIndexes={[0, 1, 3]}
            headers={['ID', 'Equipo', 'Nombre', <Icon name='wrench' />]}
            centeredIndexes={[1, 3]}
            footer={(shown_types.length >= PAGE_SIZE || page > 0) && (
               <Table.Cell colSpan={4} row>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                     <Pagination page={page} onPageChange={onPageChange} hasNext={shown_types.length >= PAGE_SIZE} />
                  </div>
               </Table.Cell>
            )}
         >
            {loading ? (
               <Table.Cell row className='empty' colSpan={4}>
                  <Header loading text='Cargando datos' />
               </Table.Cell>
            ) : (
               shown_types.length !== 0 ? (
                  shown_types.map(type =>(
                     <Table.Row
                        key={type.type_id}
                        collapsingIndexes={[0, 1, 3]}
                        data={[
                           type.type_id,
                           <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 5 }}>
                              {type.type_icon ? <Icon name={type.type_icon as IconName} /> : <Icon name='question-circle' />}
                           </div>,
                           type.type_name,
                           <Dropdown key={type.type_id} icon={(<div className='fr label' style={{ padding: '3px 10px' }}><Icon name='ellipsis' style={{ margin: 0 }} /></div>)} className='icon'>
                              <Dropdown.Menu>
                                 {hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY) && (
                                    <Dropdown.Item onClick={() =>openEdit(type)}>
                                       <Icon name='pen' style={{ marginRight: 3 }} /> Editar
                                    </Dropdown.Item>
                                 )}
                                 {hasAccess(UserAccess.INVENTORY.DELETE_INVENTORY) && (
                                    <Dropdown.Item onClick={() =>openDelete(type)}>
                                       <Icon name='trash' style={{ marginRight: 3 }} /> Eliminar
                                    </Dropdown.Item>
                                 )}
                              </Dropdown.Menu>
                           </Dropdown>
                        ]}
                     />
                  ))
               ) : (
                  <Table.Cell row className='empty' colSpan={4}>
                     No se encontraron registros
                  </Table.Cell>
               )
            )}
         </Table>
      </Groupper>
      <Modal open={showCreate} onClose={bindClick(setShowCreate, false)} size='tiny'>
         <Modal.Header>Agregar tipo</Modal.Header>
         <Modal.Content>
            <Field label='Nombre'>
               <Input value={form.type_name} onChange={onFormChange('type_name')} placeholder='Ej: Laptop' />
            </Field>
         <Field label='Icono'>
            <Dropdown
               search
               selection
               fluid
               placeholder='Selecciona un identificador de icono'
               options={iconOptions}
               value={form.type_icon}
               onChange={onIconChange}
            />
         </Field>
            <Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
         </Modal.Content>
         <Modal.Actions>
            <Button text='Cancelar' basic onClick={bindClick(setShowCreate, false)} />
            <Button text='Guardar' color='black' onClick={submitCreate} />
         </Modal.Actions>
      </Modal>
      <Modal open={showEdit} onClose={bindClick(setShowEdit, false)} size='tiny'>
         <Modal.Header>Editar tipo de inventario</Modal.Header>
         <Modal.Content>
            <Field label='Nombre'>
               <Input value={form.type_name} onChange={onFormChange('type_name')} placeholder='Ej: Laptop' />
            </Field>
            <Field label='Equipo'>
               <Dropdown
                  search
                  selection
                  fluid
                  placeholder='Selecciona un Equipo'
                  options={iconOptions}
                  value={form.type_icon || null}
                  onChange={onIconChange}
                  onOpen={() =>setOpen(true)}
                  onClose={() =>setOpen(false)}
               />
            </Field>
            <Message type='error' list={modalPrompts} />
         </Modal.Content>
         <Modal.Actions>
            <Button text='Cancelar' basic onClick={bindClick(setShowEdit, false)} />
            <Button text='Guardar' color='black' onClick={submitEdit} />
         </Modal.Actions>
      </Modal>
      <Modal open={showDelete} onClose={bindClick(setShowDelete, false)} size='tiny'>
         <Modal.Header>Eliminar tipo de inventario</Modal.Header>
         <Modal.Content>
				<Header text='¿Estás seguro de que deseas eliminar este tipo de inventario?' subtext='El tipo sera marcado como eliminado' />
            {selectedType && (
               <Table
						style={{ marginTop: 20 }}
                  details
                  title='Detalles'
                  data={[
                     ['ID', selectedType.type_id],
                     ['Nombre', selectedType.type_name],
                     ['Equipo', <Icon name={selectedType.type_icon} />]
                  ]}
               />
            )}
            <Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
         </Modal.Content>
         <Modal.Actions>
            <Button text='Cancelar' basic onClick={bindClick(setShowDelete, false)} />
            <Button text='Eliminar' color='red' onClick={submitDelete} />
         </Modal.Actions>
      </Modal>
   </div>
}

export default InventoryTypesList;
